// Avis Clients - Espace Vendeur
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Star, MessageSquare, ThumbsUp, ThumbsDown, Filter, Search,
  TrendingUp, Users, Award, Reply, Flag, CheckCircle2, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Composant étoiles
function StarRating({ rating, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
        />
      ))}
    </div>
  );
}

// Barre de progression pour rating
function RatingBar({ stars, count, total }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 w-8">{stars} ★</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-yellow-400 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm text-gray-500 w-8 text-right">{count}</span>
    </div>
  );
}

// Carte avis
function ReviewCard({ review, onReply }) {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;
    onReply(review, replyText);
    setReplyText('');
    setShowReplyInput(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <span className="text-white font-semibold">{review.author.charAt(0)}</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900">{review.author}</p>
            <div className="flex items-center gap-2">
              <StarRating rating={review.rating} size={14} />
              <span className="text-xs text-gray-400">{review.date}</span>
            </div>
          </div>
        </div>
        {review.verified && (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle2 size={12} />
            Achat vérifié
          </span>
        )}
      </div>

      {/* Produit */}
      <div className="text-sm text-gray-500 mb-3">
        Produit: <span className="text-gray-700 font-medium">{review.product}</span>
      </div>

      {/* Commentaire */}
      <p className="text-gray-700 mb-4">{review.comment}</p>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-4">
          <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <ThumbsUp size={14} /> {review.helpful || 0}
          </button>
          <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <Flag size={14} /> Signaler
          </button>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowReplyInput(!showReplyInput)}
          className="rounded-lg"
        >
          <Reply size={14} className="mr-1" />
          Répondre
        </Button>
      </div>

      {/* Réponse existante */}
      {review.reply && (
        <div className="mt-4 ml-6 pl-4 border-l-2 border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-orange-600 text-sm">Ta réponse</span>
            <span className="text-xs text-gray-400">{review.replyDate}</span>
          </div>
          <p className="text-sm text-gray-600">{review.reply}</p>
        </div>
      )}

      {/* Input réponse */}
      {showReplyInput && !review.reply && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-4 space-y-3"
        >
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Écris ta réponse..."
            rows={3}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowReplyInput(false)}
              className="rounded-lg"
            >
              Annuler
            </Button>
            <Button
              size="sm"
              onClick={handleSubmitReply}
              className="bg-orange-500 hover:bg-orange-600 rounded-lg"
            >
              Publier
            </Button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function AvisPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      // Données simulées
      setReviews([
        {
          id: '1',
          author: 'Marie Lambert',
          rating: 5,
          date: '15 Jan 2025',
          product: 'Perceuse Bosch Professional GSB 18V',
          comment: 'Excellent produit ! Puissance parfaite pour tous mes travaux de bricolage. La batterie tient vraiment longtemps. Je recommande vivement.',
          verified: true,
          helpful: 12,
          reply: null
        },
        {
          id: '2',
          author: 'Pierre Durand',
          rating: 4,
          date: '14 Jan 2025',
          product: 'Lot de 50 chevilles',
          comment: 'Bon rapport qualité-prix. Les chevilles sont solides et faciles à installer. Un peu cher peut-être mais la qualité est au rendez-vous.',
          verified: true,
          helpful: 5,
          reply: 'Merci pour ton retour Pierre ! Nous sommes ravis que les chevilles répondent à tes attentes.',
          replyDate: '14 Jan 2025'
        },
        {
          id: '3',
          author: 'Sophie Martin',
          rating: 3,
          date: '12 Jan 2025',
          product: 'Niveau laser Makita SK105DZ',
          comment: 'Produit correct mais la notice est un peu difficile à comprendre. Le laser est précis mais j\'aurais aimé un étui de transport.',
          verified: true,
          helpful: 3,
          reply: null
        },
        {
          id: '4',
          author: 'Jean Petit',
          rating: 5,
          date: '10 Jan 2025',
          product: 'Coffret tournevis Facom',
          comment: 'Qualité Facom, rien à redire. Les embouts sont nombreux et de très bonne qualité. Un investissement qui durera des années !',
          verified: true,
          helpful: 8,
          reply: null
        },
        {
          id: '5',
          author: 'Lucie Bernard',
          rating: 2,
          date: '8 Jan 2025',
          product: 'Marteau Peugeot 500g',
          comment: 'Déçue par ce produit. Le manche s\'est fissuré après quelques utilisations. Service client contacté pour un échange.',
          verified: false,
          helpful: 2,
          reply: 'Bonjour Lucie, nous sommes désolés pour ce désagrément. Notre service client t\'a contacté pour procéder à l\'échange. Merci de ta compréhension.',
          replyDate: '9 Jan 2025'
        },
      ]);
    } catch (e) {
      console.error('Error fetching reviews:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = (review, replyText) => {
    setReviews(reviews.map(r => 
      r.id === review.id 
        ? { ...r, reply: replyText, replyDate: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) }
        : r
    ));
    toast.success('Réponse publiée');
  };

  // Calcul des stats
  const stats = {
    total: reviews.length,
    average: reviews.length > 0 
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0,
    distribution: [5, 4, 3, 2, 1].map(stars => ({
      stars,
      count: reviews.filter(r => r.rating === stars).length
    })),
    pendingReplies: reviews.filter(r => !r.reply && r.rating <= 3).length
  };

  // Filtrage
  const filteredReviews = reviews.filter(r => {
    if (filter === 'pending') return !r.reply;
    if (filter === 'positive') return r.rating >= 4;
    if (filter === 'negative') return r.rating <= 2;
    return true;
  }).filter(r => 
    searchQuery === '' || 
    r.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.product.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.comment.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto" data-testid="avis-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Avis Clients</h1>
        <p className="text-gray-500">Consulte et réponds aux avis de tes clients</p>
      </div>

      {/* Stats Overview */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Note moyenne */}
        <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="text-5xl font-bold">{stats.average}</div>
            <div>
              <StarRating rating={Math.round(parseFloat(stats.average))} size={20} />
              <p className="text-white/80 mt-1">{stats.total} avis</p>
            </div>
          </div>
        </div>

        {/* Distribution */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Distribution des notes</h3>
          <div className="space-y-2">
            {stats.distribution.map(({ stars, count }) => (
              <RatingBar key={stars} stars={stars} count={count} total={stats.total} />
            ))}
          </div>
        </div>

        {/* Actions rapides */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">À traiter</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Clock size={20} className="text-orange-600" />
                <span className="text-sm text-gray-700">Avis sans réponse</span>
              </div>
              <span className="font-bold text-orange-600">{stats.pendingReplies}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
              <div className="flex items-center gap-3">
                <ThumbsDown size={20} className="text-red-600" />
                <span className="text-sm text-gray-700">Avis négatifs</span>
              </div>
              <span className="font-bold text-red-600">
                {reviews.filter(r => r.rating <= 2).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Rechercher un avis..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'all', label: 'Tous' },
            { value: 'pending', label: 'Sans réponse' },
            { value: 'positive', label: 'Positifs (4-5★)' },
            { value: 'negative', label: 'Négatifs (1-2★)' },
          ].map(item => (
            <button
              key={item.value}
              onClick={() => setFilter(item.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === item.value
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-yellow-500/20 border-t-yellow-500 rounded-full animate-spin" />
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Star size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Aucun avis</h3>
          <p className="text-gray-500">Les avis de tes clients apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onReply={handleReply}
            />
          ))}
        </div>
      )}
    </div>
  );
}
